/**
 * Featured-card connection exclusions.
 *
 * The Sessions dialog lets the user switch individual connection chips off
 * before resuming ("Restores 1 of 2 connections"). Dropping one is routed
 * through the SAME `remove` decision path the validation dialog uses
 * (`applyConnectionDecisions`), so the connection and its tabs leave the
 * session — the resume neither connects it nor keeps it. Must run BEFORE
 * validation, which connects every connection the session still declares.
 *
 * ORDER IS LOAD-BEARING: let go of the live connection FIRST, commit the
 * exclusion only for the ones that were actually released. `saveCurrentSession`
 * re-adopts every LIVE connection unconditionally, so an exclusion that left
 * the connection connected came back on the next auto-save (500ms later) —
 * the card said "Restores 1 of 2 connections", that state was committed, and
 * a background save silently reversed it in both the live and the persisted
 * session. `disconnectFromDatabase` reports an ordinary close failure by
 * RESOLVING `false` rather than rejecting, so a `try/catch` around it never
 * saw that case (codex review, E6).
 */
import { toast } from '../../../components/ui/toaster';
import { useConnectionStore } from '../../connections/store/connectionStore';
import { useSessionStore } from '../store/sessionStore';
import { applyConnectionDecisions, createExclusionDecisions } from './connectionValidationService';
import type { Connection } from '../../../types/electron';
import { safeConnectionName } from '../../../types/safeConnectionDisplay';

const connectionLabel = (savedConnections: Connection[], connectionId: string): string => {
  const saved = savedConnections.find((c) => c.id === connectionId);
  return safeConnectionName(saved, 'Connection');
};

export async function applyResumeExclusions(excludedConnectionIds: string[]): Promise<void> {
  if (excludedConnectionIds.length === 0) return;
  if (!useSessionStore.getState().currentSession) return;

  const savedConnections = useConnectionStore.getState().connections;

  const released: string[] = [];
  const stuck: string[] = [];

  for (const connectionId of excludedConnectionIds) {
    const isActive = useConnectionStore
      .getState()
      .activeConnections.some((conn) => conn.id === connectionId);
    if (!isActive) {
      released.push(connectionId);
      continue;
    }

    let closed = false;
    try {
      closed = await useConnectionStore.getState().disconnectFromDatabase(connectionId);
    } catch (error) {
      console.error(`[Sessions] Failed to disconnect excluded ${connectionId}:`, error);
    }
    (closed ? released : stuck).push(connectionId);
  }

  if (stuck.length > 0) {
    // Say what happened instead of showing an exclusion that didn't happen.
    // Excluding it again works once the driver settles: by then the pool is
    // gone and the close is a no-op that succeeds.
    const names = stuck.map((id) => connectionLabel(savedConnections, id)).join(', ');
    toast.error({
      title: stuck.length === 1 ? "Couldn't exclude a connection" : "Couldn't exclude connections",
      description: `${names} could not be closed, so ${
        stuck.length === 1 ? 'it stays' : 'they stay'
      } in the session. Try again.`,
    });
  }

  if (released.length === 0) return;

  // Re-read: the awaited disconnects can have triggered an auto-save that
  // rewrote `currentSession` underneath us.
  const session = useSessionStore.getState().currentSession;
  if (!session) return;

  useSessionStore
    .getState()
    .replaceCurrentSession(
      applyConnectionDecisions(session, createExclusionDecisions(released, savedConnections))
    );

  // Record the explicit-disconnect intent (the contract in
  // Docs/Session_Management.md) so the carry-forward union can't resurrect it.
  for (const connectionId of released) {
    useSessionStore.getState().removeConnectionFromSession(connectionId);
  }
}
